import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from './authService';

// Query keys
export const messageKeys = {
  all: ['messages'],
  conversations: () => [...messageKeys.all, 'conversations'],
  conversation: (id) => [...messageKeys.conversations(), id],
  unreadCount: () => [...messageKeys.all, 'unread-count'],
};

// API functions
export const messageApi = {
  // Get all conversations
  getConversations: async () => {
    const response = await api.get('/conversations');
    return response.data;
  },

  // Get conversation messages
  getMessages: async (conversationId) => {
    const response = await api.get(`/conversations/${conversationId}/messages`);
    return response.data;
  },

  // Send message
  sendMessage: async ({ conversationId, message }) => {
    const response = await api.post(`/conversations/${conversationId}/messages`, { message });
    return response.data;
  },

  // Mark message as read
  markAsRead: async (messageId) => {
    const response = await api.put(`/messages/${messageId}/read`);
    return response.data;
  },

  // Delete message
  deleteMessage: async (messageId) => {
    const response = await api.delete(`/messages/${messageId}`);
    return response.data;
  },

  // Get unread messages count
  getUnreadCount: async () => {
    const response = await api.get('/conversations');
    const conversations = response.data?.data || [];
    const unreadCount = conversations.reduce((count, conv) => {
      return count + (conv.unread_count || 0);
    }, 0);
    return { data: unreadCount };
  },
};

// React Query hooks
export const useConversations = () => {
  return useQuery({
    queryKey: messageKeys.conversations(),
    queryFn: messageApi.getConversations,
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

export const useMessages = (conversationId) => {
  return useQuery({
    queryKey: messageKeys.conversation(conversationId),
    queryFn: () => messageApi.getMessages(conversationId),
    enabled: !!conversationId,
    refetchInterval: 10000, // Refetch every 10 seconds
  });
};

export const useUnreadMessagesCount = () => {
  return useQuery({
    queryKey: messageKeys.unreadCount(),
    queryFn: messageApi.getUnreadCount,
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

export const useSendMessage = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ conversationId, message }) => messageApi.sendMessage({ conversationId, message }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: messageKeys.conversation(variables.conversationId) });
      queryClient.invalidateQueries({ queryKey: messageKeys.conversations() });
      queryClient.invalidateQueries({ queryKey: messageKeys.unreadCount() });
      options.onSuccess?.(data, variables);
    },
    onError: options.onError,
  });
};

export const useMarkMessageAsRead = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: messageApi.markAsRead,
    onSuccess: (data, messageId) => {
      queryClient.invalidateQueries({ queryKey: messageKeys.conversations() });
      queryClient.invalidateQueries({ queryKey: messageKeys.unreadCount() });
      options.onSuccess?.(data, messageId);
    },
    onError: options.onError,
  });
};

export const useDeleteMessage = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: messageApi.deleteMessage,
    onSuccess: (data, messageId) => {
      queryClient.invalidateQueries({ queryKey: messageKeys.conversations() });
      options.onSuccess?.(data, messageId);
    },
    onError: options.onError,
  });
};

